import { Transition } from "@remix-run/react/transition";
import { Pool } from "pg";
import { FC, useState } from "react";
import {
  ActionFunction,
  Form,
  Link,
  LoaderFunction,
  redirect,
  useActionData,
  useLoaderData,
  useTransition,
} from "remix";
import { getUserId, OptionalUser } from "~/lib/auth.server";
import { Button, PageHeader } from "~/lib/components";
import { BasicPage } from "~/lib/components/BasicPage";
import { Features, HeroHeader, TextOnlySection } from "~/lib/components/landing/features";
import {
  ActionResult,
  FormTextBox,
  CreateFormErrorForField,
  ChildActionResult,
} from "~/lib/components/remixUtil";
import { GetFormValue } from "~/lib/remixUtil.server";
import { RequestType, writeActionRequest } from "~/new_shared/db/actionRequest";
import { getPool } from "~/new_shared/db/db.server";
import { withEffectsSessions } from "~/new_shared/effects.server";
import { getEmailServer } from "~/new_shared/email/sendEmail.server";
import { teamEmailAddress, TntAllUrls } from "~/new_shared/urls";
import { createUserWithUsername, FieldError } from "~/new_shared/users.server";
import { HowItWorks } from "./content/howitworks";

interface LoaderData {}

export enum SignUpFormFieldNames {
  Email = "email",
  Username = "username",
}

export const loader: LoaderFunction = async ({ request }) => {
  const { user } = await OptionalUser(request);
  if (user) {
    return redirect(TntAllUrls.Inbox());
  }
  return {};
};

export const SignupElementId = "signup";

interface ActionData {
  childResult: ChildActionResult | undefined;
  username: string | undefined;
}

export const SignUpForm: FC<{
  childResult: ChildActionResult | undefined;
  transition: Transition;
  embedLocation: string;
}> = ({ childResult, transition, embedLocation }) => {
  const result: ActionResult | undefined = childResult && { _action: "", ...childResult };
  const [username, setUsername] = useState<string>();
  return (
    // Blockiong creation because we're getting a lot of sign up requests that
    // appear to be spam - invalid accounts getting created. Not sure how it's
    // being used for spam, but it probably isn't a good thing... 
    //
    // <Form className="" method="post">
    //   <div className="form-floating mb-3">
    //     <FormTextBox
    //       name={SignUpFormFieldNames.Username}
    //       label="Username"
    //       result={result}
    //       placeholder="Username"
    //       onChange={setUsername}
    //       pattern="[a-zA-Z0-9]{5,20}"
    //       patternErrorMessage="Username must be at least 5 characters and only letters and numbers"
    //     />
    //   </div>
    //   <div className={"form-floating text-muted my-3"}>
    //     <small>Your ThxNoThx email: {username ? username : "{username}"}@thxnothx.com</small>
    //   </div>
    //   <div className="form-floating mb-3">
    //     <FormTextBox
    //       name={SignUpFormFieldNames.Email}
    //       label="Existing email address"
    //       result={result}
    //       placeholder="you@gmail.com"
    //       type="email"
    //     />
    //   </div>

    //   <Button
    //     onClick={() => {
    //       gtag &&
    //         gtag("event", "sign_up", {
    //           method: embedLocation,
    //         });
    //     }}
    //     className="look-disabled-on-invalid"
    //   >
    //     {transition.state == "submitting" ? (
    //       <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true">
    //         working...
    //       </span>
    //     ) : (
    //       "Sign up"
    //     )}
    //   </Button>
    //   <hr className="my-4" />
    //   <small className="text-muted">
    //     No passwords: we email you a magic link. <br />
    //     We never share your email address with anyone.
    //   </small>
    // </Form>
    <div>
      ThxNoThx is not currently accepting new users. 
    </div>
  );
};

export const SignUpSection: FC = ({ children }) => {
  return (
    <div id={SignupElementId}>
      <div className="mx-auto text-center">
        <div className="mb-3">
          <HeroHeader>Try for free right now</HeroHeader>
          <p>
            ThxNoThx is $3 per month. Unsubscribes are charged separately.
            <br />
            Sign up for a free 30 day trial to see how it works for yourself
          </p>
        </div>
        <div className="p-3 p-md-8 border rounded-3 bg-light sm:w-96 mx-auto bg-neutral-50">
          {children}
        </div>
      </div>
    </div>
  );
};

export const doUserCreate = async (pool: Pool, formData: FormData): Promise<ChildActionResult> => {
  return {success: false};
  // const usernameVal = await GetFormValue(formData, SignUpFormFieldNames.Username);
  // const emailVal = await GetFormValue(formData, SignUpFormFieldNames.Email);
  // if (!usernameVal.success) {
  //   console.error("Error during account creation: username not filled out");
  //   return CreateFormErrorForField(SignUpFormFieldNames.Username, usernameVal.errorMessage);
  // }
  // if (!emailVal.success) {
  //   console.error("Error during account creation: email not filled out");
  //   return CreateFormErrorForField(SignUpFormFieldNames.Email, emailVal.errorMessage);
  // }
  // const createResult = await withEffectsSessions(
  //   pool,
  //   await getEmailServer(),
  //   async (doEffect) =>
  //     await createUserWithUsername(doEffect, emailVal.val, usernameVal.val, new Date()),
  // );
  // if (!createResult.success) {
  //   const err: FieldError = createResult.error;
  //   if (createResult.internalLog) console.log(createResult.internalLog);
  //   console.error("Error during account creation: " + JSON.stringify(err));
  //   await writeActionRequest(
  //     pool,
  //     RequestType.UnexpectedActivity,
  //     "Error during account creation",
  //     { ...err, usernameVal, emailVal },
  //   );
  //   return { success: false, errors: [err] };
  // }
  // console.log("User successfully created: " + JSON.stringify(usernameVal));
  // return { success: true };
};

export const action: ActionFunction = async ({ request }): Promise<ActionData> => {
  const pool = await getPool();
  const formData = await request.formData();
  const childResult = await doUserCreate(pool, formData);
  const username = (formData.get(SignUpFormFieldNames.Username) as string) ?? undefined;
  return { childResult, username };
};

export const SignUpSuccessMessage = ({ username }: { username: string }) => {
  return (
    <div className="text-left p-2">
      <p>We've sent a verification email - please click it to confirm your email address.</p>
      <p>Or you can get started cleaning up your inbox right now:</p>
      <ol className="">
        <li>Find an email in your inbox you want to unsubscribe from</li>
        <li>
          Forward it to <b>unsubscribe@thxnothx.com</b>
        </li>
        <li>We will unsubscribe from the mailing list for you!</li>
        <li>Repeat for as many emails as you'd like!</li>
      </ol>
    </div>
  );
};

export default function Landing() {
  const actionData = useActionData<ActionData>();
  const transition = useTransition();
  return (
    <BasicPage user={undefined}>
      <Features style={"simple"} />

      <SignUpSection>
        {actionData?.childResult?.success && actionData.username ? (
          <SignUpSuccessMessage username={actionData.username} />
        ) : (
          <SignUpForm
            childResult={actionData?.childResult}
            transition={transition}
            embedLocation={"rootLP"}
          />
        )}
      </SignUpSection>
      <div className="mt-16">
        <TextOnlySection>
          <HowItWorks user={undefined} />
        </TextOnlySection>
      </div>

      <h3 className="text-center py-8 max-w-prose mx-auto">
        Still have questions? Contact us at{" "}
        <a href={"mailto:" + teamEmailAddress}>{teamEmailAddress}</a> - we reply to every email!
      </h3>
    </BasicPage>
  );
}
